/**
 * The link from a deal in the Hub to the same deal in HubSpot.
 *
 * Hanneza, 9 September: "HubSpot acts as our official source of truth for
 * financial records, while the Hub is where we keep our internal, team-facing
 * notes... having the HubSpot link right on the card would save us a lot of
 * time searching when we need to cross-reference."
 *
 * ── An address, and nothing else ───────────────────────────────────────────
 *
 * This module stores where the record is. It does not read HubSpot, does not
 * copy an amount or a stage across, and has no idea whether the record on the
 * other end still exists. That is the point: two systems holding one number
 * disagree eventually, and a link cannot.
 *
 * ── Why the host is checked ────────────────────────────────────────────────
 *
 * A field labelled HubSpot that silently accepts a Google Doc is worse than
 * one that refuses it, because the mistake is invisible until somebody clicks
 * it mid-call. The check is on the HOST, never the whole string — matching
 * anywhere would accept `example.com/why-we-left-hubspot`, which is the same
 * bug resourceLinks.ts documents and avoids the same way.
 *
 * Any HubSpot subdomain passes. Their portals are regional and the host
 * differs per account (app.hubspot.com, app-eu1.hubspot.com, and so on), so
 * pinning one would refuse links that are perfectly correct for somebody
 * else's portal.
 */

const HUBSPOT_HOST = 'hubspot.com';

/** What the drawer stores, or nothing, or a refusal. Mirrors parseAmount. */
export function parseHubspotUrl(raw: string): string | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') {
    // Clearing the field unlinks the deal. Not an error.
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return undefined;
  }

  // http is refused rather than upgraded. Silently rewriting what somebody
  // pasted means the address stored is not the address they checked.
  if (parsed.protocol !== 'https:') {
    return undefined;
  }

  const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
  if (host !== HUBSPOT_HOST && !host.endsWith(`.${HUBSPOT_HOST}`)) {
    return undefined;
  }

  // Stored as typed apart from surrounding space. Query strings and fragments
  // are kept: HubSpot puts real navigation in both, and trimming them would
  // land somebody on the portal's front page instead of the record.
  return trimmed;
}

/** Why a link was refused, in words somebody can act on. */
export function hubspotLinkError(raw: string): string {
  const trimmed = raw.trim();
  if (parseHubspotUrl(trimmed) !== undefined) {
    return '';
  }
  let parsed: URL | null;
  try {
    parsed = new URL(trimmed);
  } catch {
    parsed = null;
  }
  if (parsed === null) {
    return `"${trimmed}" is not a web address. Open the deal in HubSpot and paste the address from the browser bar.`;
  }
  if (parsed.protocol !== 'https:') {
    return 'That link is not https. Copy the address straight out of the browser bar rather than typing it.';
  }
  return `That is a ${parsed.hostname.replace(/^www\./, '')} link, not a HubSpot one. This field is for the deal's HubSpot record.`;
}

/**
 * What to show on a card: enough to recognise, short enough to sit in a foot
 * row. Never the whole URL — a HubSpot deal address is ~70 characters and
 * would set the width of every card on the board.
 */
export function hubspotLinkLabel(url: string | null): string {
  return url === null || url.trim() === '' ? '' : 'HubSpot';
}
